/**
 * Integrations, connected credentials, Skills and Search.
 *
 * One half of `AGENT_COPY`. The shape and the rules are documented on the
 * barrel in `../agent-copy.ts`; this file only holds entries.
 *
 * One vocabulary across the three screens: an integration is 集成, the thing it
 * authenticates with is a 凭据, a service account is a 服务账号, a skill is a
 * 技能, and a Search source is a 数据源. A registry's own words — a block's
 * name, a category, a connector's description — are not here; they are data.
 */
export const INTEGRATIONS: Record<string, string> = {
	"Web Search": "网页搜索",
	"Web Content": "网页内容",
	"Web Research": "网络研究",
	"Search and Video": "搜索与视频",
	"Keyword Data": "关键词数据",
	"Google Search": "Google 搜索",
	"Web Contents": "网页正文",
	"Web Extract": "网页提取",
	"Website Map": "网站链接",
	"Web Answer": "网络问答",
	"Research Report": "研究报告",
	"YouTube Video": "YouTube 视频信息",
	"YouTube Transcript": "YouTube 视频字幕",
	"Keyword Ideas": "关键词建议",
	"Keyword Volume": "关键词搜索量",
	"Input Parameters (JSON)": "输入参数（JSON）",
	"Search web pages, read content, and answer questions with sources.": "搜索网页、读取正文，并依据来源回答问题。",
	"Extract webpage text, discover URLs, and search the web.": "提取网页正文、发现网站链接并搜索网页。",
	"Search the web, extract page content, and create research reports.": "搜索网页、提取正文并生成研究报告。",
	"Search Google for pages relevant to a query.": "在 Google 中搜索与查询相关的网页。",
	"Search Google and read video information and transcripts.": "搜索 Google，读取视频信息和字幕。",
	"Find keyword ideas and measure search volume.": "发现关键词建议并查询搜索量。",
	"Optional complete input object. Replaces the fields above when provided.":
		"可选的完整输入对象，填写后将替代上方字段。",
	Feishu: "飞书",
	DingTalk: "钉钉",
	WeCom: "企业微信",
	"self-built app": "自建应用",
	"Read Feishu document": "读取飞书文档",
	"Send Feishu message": "发送飞书消息",
	"Read DingTalk table": "读取钉钉表格",
	"Read DingTalk document blocks": "读取钉钉文档块",
	"Send DingTalk message": "发送钉钉消息",
	"Read WeCom document": "读取企业微信文档",
	"Send WeCom message": "发送企业微信消息",
	"Feishu account": "飞书账号",
	"DingTalk account": "钉钉账号",
	"WeCom account": "企业微信账号",
	"Cell range": "单元格范围",
	"Operator unionId": "操作人 unionId",
	"Robot code": "机器人编码",
	"Group conversation ID": "群会话 ID",
	"Recipient user IDs": "接收成员 ID",
	"Message text": "消息正文",
	"Workbook ID": "工作簿 ID",
	"Sheet ID": "工作表 ID",
	"Enter the document ID": "填写文档 ID",
	"Enter the message text": "填写消息正文",
	"Enter the workbook ID": "填写工作簿 ID",
	"Enter the sheet ID": "填写工作表 ID",
	"Enter the authorized operator unionId": "填写有权访问的操作人 unionId",
	"Enter the enterprise app robot code": "填写企业应用机器人编码",
	"Paste the app secret": "粘贴应用密钥",
	"Application Secret": "应用 Secret",
	"Read Feishu documents and send chat messages": "读取飞书文档并发送群聊消息",
	"Read DingTalk tables and document blocks, and send robot messages": "读取钉钉表格、文档块并发送机器人消息",
	"Read WeCom documents and send application messages": "读取企业微信文档并发送应用消息",
	"Connect a Feishu self-built app to read authorized docx documents and send text messages to chats. Wiki node tokens must first be resolved to docx document IDs.":
		"连接飞书自建应用，读取已授权的新版文档并发送群聊文本消息。知识库节点 token 需先转换为 docx 文档 ID。",
	"Connect a DingTalk internal app to read online table ranges, query document suite blocks, and send robot group messages. Document and table reads require an authorized operator unionId.":
		"连接钉钉企业内部应用，读取在线表格区域、查询文档套件中的文档块，并发送机器人群消息。读取文档和表格需要填写有权访问的操作人 unionId。",
	"Connect a WeCom self-built app to read authorized WeDoc documents and send text messages to app users. Enable document API access for this application in the enterprise administration console.":
		"连接企业微信自建应用，读取已授权的企业微信文档并向应用成员发送文本消息。请先在企业管理后台将此应用加入文档 API 的可调用应用列表。",
	// -- integrations catalogue
	Featured: "精选",
	"Search integrations...": "搜索集成…",
	"Explain the integrations in Ondit and what I should connect.": "介绍一下 Ondit 里的集成，以及我应该连接哪些。",
	"Explore in chat": "在对话中探索",
	"Unavailable in this deployment. Contact your administrator.": "此部署未提供该集成，请联系管理员。",
	"No integrations found matching “{term}”": "没有找到与“{term}”匹配的集成",
	"No integrations in this category": "此分类下暂无集成",
	"Failed to load integrations": "集成加载失败",
	"Something went wrong while loading your integrations. Please try again.": "加载集成时出错，请重试。",

	// -- an integration's detail page
	"Add to Ondit": "添加到 Ondit",
	"Add personal token": "添加个人令牌",
	"Connect with OAuth": "使用 OAuth 连接",
	"Add service account": "添加服务账号",
	"Add {noun}": "添加{noun}",
	"Set up a custom bot": "配置自定义机器人",
	Unavailable: "不可用",
	"Explore {name}. What can I do?": "介绍一下 {name}，我能用它做什么？",
	"Use template {title}": "使用模板 {title}",
	Add: "添加",
	"Adding...": "添加中…",
	"“{name}” is already in this workspace": "“{name}”已存在于此工作区",
	"Failed to add “{name}” — please try again": "添加“{name}”失败，请重试",

	// -- connecting a Shopify store
	"Store domain": "店铺域名",
	// -- connecting a WordPress site
	site: "站点",
	"Site URL": "站点网址",
	Username: "用户名",
	"Application password": "应用密码",
	"Create an application password under Users → Profile in WordPress. It is not your login password.":
		"在 WordPress 的「用户 → 个人资料」中创建应用密码，不是登录密码。",
	// -- connecting a GitLab personal token
	"Connect your GitLab account": "连接你的 GitLab 账号",
	"GitLab host": "GitLab 主机",
	"Personal access token": "个人访问令牌",
	"Only you can use this connection.": "此连接仅你本人可用。",
	"Create a token": "创建令牌",

	// -- connecting a service account
	"Add {service} service account": "添加 {service} 服务账号",
	"Add {service} {noun}": "添加 {service}{noun}",
	"JSON key": "JSON 密钥",
	"Paste your service account JSON key here": "在此粘贴服务账号的 JSON 密钥",
	"Or upload a file": "或上传文件",
	"Drag & drop a .json file, or click to browse": "拖入 .json 文件，或点击选择",
	"Uploaded {file} — click or drop to replace": "已上传 {file} — 点击或拖入可替换",
	"Only .json files are supported": "仅支持 .json 文件",
	"Paste the service account JSON key.": "请粘贴服务账号的 JSON 密钥。",
	"Invalid JSON": "JSON 格式无效",
	"Failed to add service account": "添加服务账号失败",
	"API token": "API 令牌",
	"Paste API token": "粘贴 API 令牌",
	"Site domain": "站点域名",
	"Atlassian sites usually look like your-team.atlassian.net.": "Atlassian 站点通常形如 your-team.atlassian.net。",
	"Display name": "显示名称",
	"Auto-populated from client_email": "自动取自 client_email",
	"Defaults to the account's Atlassian display name": "默认使用该账号的 Atlassian 显示名称",
	"Defaults to the {service} account name": "默认使用该 {service} 账号的名称",
	"Optional description": "描述（可选）",
	host: "主机",
	"We couldn't authenticate with that API token. Double-check the token and that the service account has access to this site.":
		"无法使用该 API 令牌通过验证。请核对令牌，并确认该服务账号有权访问此站点。",
	"We couldn't find an Atlassian site at that domain. Check the spelling — it should look like your-team.atlassian.net.":
		"在该域名下找不到 Atlassian 站点。请检查拼写，它应形如 your-team.atlassian.net。",
	"We couldn't reach Atlassian to verify these credentials. Try again in a moment.":
		"无法连接 Atlassian 验证这些凭据，请稍后重试。",
	"We couldn't add this service account. Try again in a moment.": "无法添加此服务账号，请稍后重试。",
	"We couldn't add this credential. Try again in a moment.": "无法添加此凭据，请稍后重试。",
	"A credential with that name already exists in this workspace.": "此工作区中已存在同名凭据。",
	"We couldn't authenticate with those credentials. Check that the {fields} all belong to the same {service} app and that the app is authorized.":
		"无法使用这些凭据通过验证。请确认 {fields} 都属于同一个 {service} 应用，且该应用已获授权。",
	"We couldn't find a {service} account at that host. Check the {field} field and try again.":
		"在该主机上找不到 {service} 账号。请检查“{field}”后重试。",
	"We couldn't reach {service} to verify these credentials. Try again in a moment.":
		"无法连接 {service} 验证这些凭据，请稍后重试。",

	// -- the custom Slack bot wizard
	"Set up Slack for search": "配置用于搜索的 Slack",
	"Create a custom Slack bot": "创建自定义 Slack 机器人",
	"Reconnect a custom Slack bot": "重新连接自定义 Slack 机器人",
	"Name your Slack app": "为 Slack 应用命名",
	"Configure your bot": "配置机器人",
	"App name": "应用名称",
	"Bot name": "机器人名称",
	"Optional — shown on the bot's Slack profile": "可选，将显示在机器人的 Slack 资料中",
	"Additional permissions": "附加权限",
	"All additional permissions enabled — the bot can read messages, react, access files and users, and people can authorize it through Connected accounts.":
		"已启用全部附加权限 — 机器人可以读取消息、添加表情回应、访问文件与用户，成员也可以在“已连接账号”中为它授权。",
	"No additional permissions": "无附加权限",
	"Member access": "成员访问方式",
	"Search documents": "搜索文档",
	"Workflow tools": "工作流工具",
	"Choose the same access when configuring this app for member accounts.":
		"为成员账号配置此应用时，请选择相同的访问方式。",
	"Slash commands (optional)": "斜杠命令（可选）",
	"Slash command {n}": "第 {n} 条斜杠命令",
	"Slash command {n} description": "第 {n} 条斜杠命令的描述",
	"Slash command {n} usage hint": "第 {n} 条斜杠命令的用法提示",
	"Remove slash command {n}": "删除第 {n} 条斜杠命令",
	"Short description shown in Slack": "在 Slack 中显示的简短描述",
	"Usage hint (optional), e.g. question or task": "用法提示（可选），例如 question 或 task",
	"Every slash command needs a command and description.": "每条斜杠命令都需要填写命令和描述。",
	"Slash commands must be one word beginning with /.": "斜杠命令必须是以 / 开头的单个词。",
	"Slash commands must be 32 characters or fewer.": "斜杠命令不能超过 32 个字符。",
	"Each slash command must be unique.": "每条斜杠命令必须唯一。",
	"Slack Agent View descriptions must be 300 characters or fewer.": "Slack Agent View 的描述不能超过 300 个字符。",
	"Create the app in Slack": "在 Slack 中创建应用",
	"Copy your manifest:": "复制应用清单：",
	"Open the": "打开",
	"Slack Apps page": "Slack Apps 页面",
	Click: "点击",
	"and pick your workspace.": "然后选择你的工作区。",
	"Paste your manifest, then click": "粘贴清单，然后点击",
	"Paste your Signing Secret": "粘贴 Signing Secret",
	"In your new Slack app, open": "在新建的 Slack 应用中打开",
	"and click": "然后点击",
	", then copy it.": "然后复制它。",
	"Paste it into the field below.": "把它粘贴到下面的输入框。",
	"Signing Secret": "Signing Secret",
	"Paste your signing secret": "粘贴 signing secret",
	"Install and paste your Bot Token": "安装应用并粘贴 Bot Token",
	"In Slack, open": "在 Slack 中打开",
	"and authorize.": "并完成授权。",
	"Copy the": "复制",
	"(starts with": "(开头为",
	"Paste it into the field below, then click Next.": "把它粘贴到下面的输入框，然后点击“下一步”。",
	"Bot Token": "Bot Token",
	"All set": "全部完成",
	"Verifying your bot and connecting…": "正在验证并连接你的机器人…",
	"Could not connect the Slack bot.": "无法连接该 Slack 机器人。",
	"Slack app connected": "Slack 应用已连接",
	"Bot connected": "机器人已连接",
	"Click Done to verify member access.": "点击“已完成”以验证成员访问权限。",
	"It's now selectable in Slack triggers and actions across this workspace. Click Done to finish.":
		"此工作区的 Slack 触发器和动作现在都可以选用它。点击“已完成”结束配置。",

	// -- a connected credential
	"Credential not found.": "找不到该凭据。",
	"Unknown service": "未知服务",
	"Connected service": "已连接的服务",
	"Credential ID": "凭据 ID",
	"Copy credential ID": "复制凭据 ID",
	"Display Name": "显示名称",
	"Add a description...": "添加描述…",
	"GitLab instance": "GitLab 实例",
	"Copy GitLab instance": "复制 GitLab 实例地址",
	Disconnect: "断开连接",
	"Disconnecting...": "断开中…",
	"Disconnect Integration": "断开集成连接",
	"Are you sure you want to disconnect ": "确定要断开 ",
	"? This action cannot be undone.": " 的连接吗？此操作无法撤销。",
	"Couldn't start reconnect": "无法开始重新连接",
	"Couldn't disconnect": "断开连接失败",
	"Please try again in a moment.": "请稍后重试。",

	// -- skills
	"Search skills...": "搜索技能…",
	"Explain the skills in Ondit and which ones I should add to my agents.":
		"介绍一下 Ondit 里的技能，以及我应该为智能体添加哪些。",
	"No skills found matching “{term}”": "没有找到与“{term}”匹配的技能",
	"Failed to load skills": "技能加载失败",
	"Something went wrong while loading your skills. Please try again.": "加载技能时出错，请重试。",
	"New skill": "新建技能",
	"Write a skill, or import an existing SKILL.md": "编写一个技能，或导入已有的 SKILL.md",
	"Lowercase letters, numbers, and hyphens (e.g. my-skill)": "仅限小写字母、数字和连字符（例如 my-skill）",
	"What this skill does and when to use it...": "这个技能做什么、什么时候用…",
	"Skill instructions in markdown...": "以 Markdown 编写的技能说明…",
	"Name must be 64 characters or less": "名称不能超过 64 个字符",
	"Name must be kebab-case (e.g. my-skill)": "名称必须为 kebab-case（例如 my-skill）",
	"Description is required": "请填写描述",
	"Create skill": "创建技能",
	"Content is required": "请填写内容",
	"This skill name is already taken.": "该技能名称已被占用。",
	"Created “{name}”": "已创建“{name}”",
	"Saved “{name}”": "已保存“{name}”",
	"Couldn't create skill": "创建技能失败",
	"Couldn't save skill": "保存技能失败",
	"Couldn't delete skill": "删除技能失败",
	"Failed to save skill. Please try again.": "保存技能失败，请重试。",
	"Skill not found.": "找不到该技能。",
	"Built-in skill": "内置技能",
	"Built-in skills are read-only": "内置技能为只读",
	"You need to be a skill editor to edit this skill": "只有技能编辑者才能编辑此技能",
	"Skill Editors ({count})": "技能编辑者（{count}）",
	"Couldn't load editors. You may no longer have access to this skill.":
		"无法加载编辑者名单，你可能已失去对该技能的访问权限。",
	"Delete Skill": "删除技能",
	"Add Skill": "添加技能",
	"Edit Skill": "编辑技能",
	Import: "导入",
	"Importing…": "导入中…",
	"Upload File": "上传文件",
	".md file with YAML frontmatter, or .zip containing a SKILL.md":
		"带 YAML frontmatter 的 .md 文件，或包含 SKILL.md 的 .zip",
	"Couldn't import skill": "导入技能失败",
	"Please try a .md or .zip file.": "请改用 .md 或 .zip 文件。",
	"Failed to process file": "文件处理失败",
	"Unsupported file type. Use .md or .zip files.": "不支持的文件类型，请使用 .md 或 .zip 文件。",
	"ZIP file is too large (max 5 MB)": "ZIP 文件过大（最大 5 MB）",
	"No SKILL.md file found in the ZIP archive": "ZIP 压缩包中没有找到 SKILL.md 文件",

	// -- Ondit Search
	"Failed to load Ondit Search": "Ondit 搜索加载失败",
	"Something went wrong while loading your Ondit Search connectors. Please try again.":
		"加载 Ondit 搜索的连接器时出错，请重试。",
	"Search sources": "搜索数据源",
	"Add source": "添加数据源",
	"Find a source...": "查找数据源…",
	"Find a source…": "查找数据源…",
	"Find a source": "查找数据源",
	Source: "数据源",
	"Source setup": "数据源配置",
	"Connection availability": "连接可用性",
	"Loading sources…": "正在加载数据源…",
	"Loading source…": "正在加载数据源…",
	"Could not load sources": "无法加载数据源",
	"Could not load more sources": "无法加载更多数据源",
	"Could not load source setup": "无法加载数据源配置",
	"Could not load connection availability": "无法加载连接可用性",
	"Could not load sources shared with you": "无法加载共享给你的数据源",
	"Load more": "加载更多",
	"No matching sources.": "没有匹配的数据源。",
	"This source is no longer available.": "该数据源已不可用。",
	"Add a source to start indexing documents for Search.": "添加数据源，开始为搜索建立文档索引。",
	"Your workspace hasn’t added any sources yet. Ask a workspace admin to get started.":
		"此工作区还没有添加任何数据源。请联系工作区管理员开始配置。",
	"Configure {source}": "配置 {source}",
	"Set up": "配置",
	"Continue setup": "继续配置",
	"Continue Search setup": "继续搜索配置",
	"Not available in this workspace": "在此工作区中不可用",
	"Not available in this organization": "在此组织中不可用",
	"Available in its knowledge base": "可在其知识库中使用",
	"Deactivated by an organization admin": "已被组织管理员停用",
	"Syncing is paused": "同步已暂停",
	"Verify your email to search this source": "验证邮箱后即可搜索此数据源",
	"Your access was removed by an admin": "管理员已移除你的访问权限",
	"Needs admin attention": "需要管理员处理",
	"Finish connecting in the other tab": "请在另一个标签页中完成连接",
	"Your account needs to be reconnected": "你的账号需要重新连接",
	"Connect your account to search this source": "连接你的账号即可搜索此数据源",
	"Connect account": "连接账号",
	"Open again": "重新打开",
	"Sync needs attention · {count}": "同步需要处理 · {count}",
	"Sync needs admin attention": "同步需要管理员处理",
	"{count} searchable document": "{count} 个可搜索文档",
	"{count} searchable documents": "{count} 个可搜索文档",
	"{count} document couldn't be indexed": "{count} 个文档未能建立索引",
	"{count} documents couldn't be indexed": "{count} 个文档未能建立索引",
	"Indexing · {count}": "建立索引中 · {count}",
	"No searchable documents yet": "暂无可搜索的文档",
	"Waiting for the first sync": "等待首次同步",
	Manage: "管理",
	"Manage source": "管理数据源",
	"{name} source actions": "{name} 数据源操作",
	"{source} sources": "{source} 数据源",
	"Sync status": "同步状态",
	"Start searching": "开始搜索",
	"Shared with you": "共享给你的",
	"Connected.": "已连接。",
};
